function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useContext, useEffect, useRef } from 'react';
import cn from 'clsx';
import { string, arrayOf, shape } from 'prop-types';
import { useTranslation } from 'react-i18next';
import List from '../../molecules/List/index.jsx';
import Icon from '../../atoms/Icon/index.jsx';
import MenuLink from '../../atoms/MenuLink/index.jsx';
import PopupBackdrop from '../../atoms/PopupBackdrop/index.jsx';
import PopupContext from '../../context/PopupContext.js';
import withAccessibleMenu from '../../hoc/withAccessibleMenu/index.js';
import { useIsDesktop } from '../../hooks/index.js';

/**
 * Count an item and all of its descendants.
 */
const countDescendants = menuItems => menuItems.reduce((prev, curr) => {
  const n = curr.data.backLink ? 0 : 1;
  return n + prev + countDescendants(curr.children || []);
}, 0);

/**
 * Get the number of columns to use for the desktop menu.
 */
const getNumberOfColumns = (menuItems = []) => {
  const total = countDescendants(menuItems);
  const nItems = menuItems.filter(item => !item.data.backLink).length;
  if (total > 32 && nItems > 3) {
    return 4;
  }
  if (total > 16 && nItems > 2) {
    return 3;
  }
  if (total > 4 && nItems > 1) {
    return 2;
  }
  return 1;
};

/**
 * The caret icon used at multiple levels.
 */
const caretIcon = /*#__PURE__*/React.createElement(Icon, {
  className: "main-nav__nav-icon",
  name: "caret",
  color: "inherit",
  size: 12
});

/**
 * MainNav component.
 */
const MainNav = withAccessibleMenu(({
  accessibleMenuItems,
  onClose,
  className
}) => {
  const {
    t
  } = useTranslation();
  const {
    activePopup,
    popupKeys
  } = useContext(PopupContext);
  const containerRef = useRef(null);
  const megaMenuRefs = [];
  const isDesktop = useIsDesktop();
  useEffect(() => {
    if (activePopup !== popupKeys.mainNav && onClose) {
      onClose();
    }
  }, [activePopup]);
  useEffect(() => {
    if (typeof window.IntersectionObserver === 'undefined') {
      return () => {};
    }
    const observer = new IntersectionObserver(entries => {
      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;

      // Uses left instead of x for IE support
      // https://caniuse.com/#feat=getboundingclientrect
      const containerLeft = containerRect.left;
      entries.forEach(item => {
        const {
          target
        } = item;
        const {
          width,
          left
        } = item.boundingClientRect;
        const pos = width + left - containerLeft; // Position of the expanded mega menu

        if (containerWidth && pos && pos > containerWidth && !target.style.left && isDesktop) {
          target.style.left = `${containerWidth - width}px`;
          target.classList.add('main-nav__mega-menu--fixed');
        }
      });
    }, {
      threshold: [0, 1]
    });
    megaMenuRefs.filter(megaMenuRef => megaMenuRef.current).forEach(megaMenuRef => {
      observer.observe(megaMenuRef.current);
    });

    // Clean up
    return () => {
      observer.disconnect();
    };
  }, [containerRef]);
  return /*#__PURE__*/React.createElement("nav", {
    className: cn('main-nav', className, activePopup === popupKeys.mainNav && 'main-nav--open'),
    id: `${popupKeys.mainNav}-popup`,
    "aria-label": t('organisms.mainNav.ariaLabel.main'),
    "data-feature": "MainNavigation"
  }, /*#__PURE__*/React.createElement("div", {
    className: "main-nav__container container container--no-gutters",
    ref: containerRef
  }, /*#__PURE__*/React.createElement(List, {
    className: "main-nav__nav",
    role: accessibleMenuItems.length ? 'menubar' : null
  }, accessibleMenuItems.map((tierOneItem, tierOneIndex) => {
    const megaMenuRef = useRef(null);
    if (tierOneItem.hasChildren) {
      megaMenuRefs.push(megaMenuRef);
    }
    const tier1Index = tierOneIndex + 1;
    return /*#__PURE__*/React.createElement(List.Item, {
      className: cn('main-nav__nav-item', tierOneItem.data.mobile && 'main-nav__nav-item--mobile', tierOneItem.hasChildren && 'main-nav__nav-item--has-children', tierOneItem.data.color && `main-nav__nav-item--wildcard-${tierOneItem.data.color}`),
      role: "presentation",
      key: tierOneItem.key,
      "data-index": tier1Index,
      "data-item-name": tierOneItem.data.label,
      "data-component-variant": tierOneItem.data.color && !tierOneItem.data.mobile ? 'Wildcard' : null,
      "data-component": "MenuLink"
    }, /*#__PURE__*/React.createElement(MenuLink, _extends({
      className: 'main-nav__nav-link' + (tierOneItem.isActiveSubMenu() ? " main-nav__nav-link--show-diamond" : ""),
      role: "menuitem"
    }, tierOneItem.linkProps), /*#__PURE__*/React.createElement("span", {
      className: "main-nav__nav-text"
    }, tierOneItem.data.label), tierOneItem.hasChildren && caretIcon), tierOneItem.hasChildren && /*#__PURE__*/React.createElement("div", {
      className: `main-nav__sub-menu main-nav__mega-menu main-nav__mega-menu--cols-${getNumberOfColumns(tierOneItem.children)}` + (tierOneItem.isActiveSubMenu() ? " main-nav__sub-menu--active" : ""),
      ref: megaMenuRef
    }, /*#__PURE__*/React.createElement(List, {
      className: "main-nav__list",
      role: tierOneItem.children.length ? 'menu' : null
    }, tierOneItem.children.map((tierTwoItem, tierTwoIndex) => {
      const tier2Index = tierTwoItem.data.backLink ? null : `${tier1Index}.${tierTwoIndex}`;
      return /*#__PURE__*/React.createElement(List.Item, {
        className: cn("main-nav__nav-item main-nav__nav-item--tier-one", tierTwoItem.hasChildren && 'main-nav__nav-item--has-children', tierTwoItem.data.backLink && 'main-nav__nav-item main-nav__nav-item--back'),
        role: "presentation",
        key: tierTwoItem.key,
        "data-index": tier2Index,
        "data-item-name": tierTwoItem.data.label
      }, /*#__PURE__*/React.createElement(MenuLink, _extends({
        className: "main-nav__nav-link",
        role: "menuitem"
      }, tierTwoItem.linkProps), tierTwoItem.data.backLink && /*#__PURE__*/React.createElement(React.Fragment, null, caretIcon, /*#__PURE__*/React.createElement("span", {
        className: "sr-only"
      }, t('organisms.mainNav.sr.tierTwoBack'), ' ')), /*#__PURE__*/React.createElement("span", {
        className: "main-nav__nav-text"
      }, tierTwoItem.data.label), tierTwoItem.hasChildren && caretIcon), tierTwoItem.hasChildren && /*#__PURE__*/React.createElement("div", {
        className: 'main-nav__sub-menu' + (tierTwoItem.isActiveSubMenu() ? " main-nav__sub-menu--active" : "")
      }, /*#__PURE__*/React.createElement(List, {
        className: "main-nav__list",
        role: tierTwoItem.children.length ? 'menu' : null
      }, tierTwoItem.children.map((tierThreeItem, tierThreeIndex) => {
        const tier3Index = tierThreeItem.data.backLink ? null : `${tier2Index}.${tierThreeIndex}`;
        return /*#__PURE__*/React.createElement(List.Item, {
          className: "main-nav__nav-item main-nav__nav-item--tier-two" + (tierThreeItem.data.backLink ? " main-nav__nav-item main-nav__nav-item--back" : ""),
          role: "presentation",
          key: tierThreeItem.key,
          "data-index": tier3Index,
          "data-item-name": tierThreeItem.data.label
        }, /*#__PURE__*/React.createElement(MenuLink, _extends({
          className: "main-nav__nav-link",
          role: "menuitem"
        }, tierThreeItem.linkProps), tierThreeItem.data.backLink && /*#__PURE__*/React.createElement(React.Fragment, null, caretIcon, /*#__PURE__*/React.createElement("span", {
          className: "sr-only"
        }, t('organisms.mainNav.sr.tierThreeBack'), ' ')), /*#__PURE__*/React.createElement("span", {
          className: "main-nav__nav-text"
        }, tierThreeItem.data.label)));
      }))));
    }))));
  }))), /*#__PURE__*/React.createElement(PopupBackdrop, {
    className: "main-nav__backdrop",
    active: activePopup === popupKeys.mainNav
  }));
}, {
  // withAccessibleMenu options
  withBackLinks: true
});
MainNav.displayName = 'MainNav';
MainNav.defaultProps = {
  menuItems: [],
  mobileMenuItems: [],
  className: null
};
MainNav.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The main menu items. */
  menuItems: arrayOf(shape(MenuLink.propTypes)),
  /** Menu items to rendered on mobile only. */
  mobileMenuItems: arrayOf(shape(MenuLink.propTypes)),
  /** Additional class names to add to the component root. */
  className: string
} : {};
export default MainNav;